"use client";

import { useState } from "react";
import {
  Checkbox,
  Notice,
  PillGroup,
  SelectField,
  TextAreaField,
  TextField,
} from "@/components/tool-kit/primitives";
import { BUTTON_PRIMARY, HINT, WIDGET_H2 } from "@/components/tool-kit/styles";
import TChartSheet from "@/components/tools/games-organizers/t-chart-sheet";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import {
  type Direction,
  drawingRatio,
  type Paper,
} from "@/lib/tool-kit/games-organizers/sheet";
import {
  cleanHeading,
  DEFAULT_ROWS,
  layoutRows,
  MAX_ROWS,
  MIN_ROWS,
  PRESETS,
  type Preset,
} from "@/lib/tool-kit/games-organizers/t-chart";
import { worksheetPrintCss } from "@/lib/tool-kit/worksheets/layout";

/** English strings. A translated content file overrides these through `ui`. */
export const T_CHART_MAKER_STRINGS = {
  presetLabel: "Chart type",
  custom: "Custom headings",
  presets: {
    prosCons: { label: "Pros and cons", left: "Pros", right: "Cons" },
    factsOpinions: {
      label: "Facts and opinions",
      left: "Facts",
      right: "Opinions",
    },
    advantagesDisadvantages: {
      label: "Advantages and disadvantages",
      left: "Advantages",
      right: "Disadvantages",
    },
    causeEffect: { label: "Cause and effect", left: "Cause", right: "Effect" },
    problemSolution: {
      label: "Problem and solution",
      left: "Problem",
      right: "Solution",
    },
    beforeAfter: { label: "Before and after", left: "Before", right: "After" },
    forAgainst: { label: "For and against", left: "For", right: "Against" },
  },
  titleLabel: "Title or topic (optional)",
  titlePlaceholder: "Should our school start later?",
  leftLabel: "Left heading",
  rightLabel: "Right heading",
  rowsLabel: "Rows",
  entriesHeading: "Fill in entries (optional)",
  entriesHint:
    "One entry per line. Leave both boxes empty to print blank rows for students.",
  leftEntries: "Left column entries",
  rightEntries: "Right column entries",
  paperLabel: "Paper",
  papers: { letter: "US Letter", a4: "A4" },
  directionLabel: "Orientation",
  directions: { portrait: "Portrait", landscape: "Landscape" },
  numbered: "Number the rows",
  nameLine: "Name and date line",
  print: "Print chart",
  heading: "Your chart",
  grew: "The chart has {count} rows to hold your entries.",
  cut: "Entries beyond {max} rows were left out: {count}.",
  sheetLabel: "T-chart: {left} and {right}",
  name: "Name",
  date: "Date",
} as const;

type Choice = Preset | "custom";

export default function TChartMakerWidget() {
  const strings = useToolUi(T_CHART_MAKER_STRINGS);
  const fmt = useToolNumber();
  const [choice, setChoice] = useState<Choice>("prosCons");
  const [left, setLeft] = useState<string>(strings.presets.prosCons.left);
  const [right, setRight] = useState<string>(strings.presets.prosCons.right);
  const [title, setTitle] = useState("");
  const [rows, setRows] = useState(DEFAULT_ROWS);
  const [leftText, setLeftText] = useState("");
  const [rightText, setRightText] = useState("");
  const [paper, setPaper] = useState<Paper>("letter");
  const [direction, setDirection] = useState<Direction>("portrait");
  const [numbered, setNumbered] = useState(false);
  const [nameLine, setNameLine] = useState(true);

  const layout = layoutRows(leftText, rightText, rows);
  const grew = layout.count > rows;
  const ratio = drawingRatio(paper, direction);

  function pick(next: Choice) {
    setChoice(next);
    if (next !== "custom") {
      setLeft(strings.presets[next].left);
      setRight(strings.presets[next].right);
    }
  }

  return (
    <div className="space-y-6">
      <style>{worksheetPrintCss(paper, direction)}</style>
      <div className="space-y-4 print:hidden">
        <div className="grid gap-4 sm:grid-cols-3">
          <SelectField
            id="tchart-preset"
            label={strings.presetLabel}
            onChange={pick}
            options={[
              ...PRESETS.map((value) => ({
                value,
                label: strings.presets[value].label,
              })),
              { value: "custom" as const, label: strings.custom },
            ]}
            value={choice}
          />
          <TextField
            id="tchart-left"
            label={strings.leftLabel}
            onChange={(value) => {
              setLeft(value);
              setChoice("custom");
            }}
            value={left}
          />
          <TextField
            id="tchart-right"
            label={strings.rightLabel}
            onChange={(value) => {
              setRight(value);
              setChoice("custom");
            }}
            value={right}
          />
        </div>
        <TextField
          id="tchart-title"
          label={strings.titleLabel}
          onChange={setTitle}
          placeholder={strings.titlePlaceholder}
          value={title}
        />
        <div className="grid gap-4 sm:grid-cols-3">
          <SelectField
            id="tchart-rows"
            label={strings.rowsLabel}
            onChange={(value) => setRows(Number(value))}
            options={Array.from(
              { length: MAX_ROWS - MIN_ROWS + 1 },
              (_, i) => ({
                value: String(MIN_ROWS + i),
                label: fmt(MIN_ROWS + i),
              })
            )}
            value={String(rows)}
          />
          <PillGroup
            label={strings.paperLabel}
            name="tchart-paper"
            onChange={setPaper}
            options={(["letter", "a4"] as const).map((value) => ({
              value,
              label: strings.papers[value],
            }))}
            value={paper}
          />
          <PillGroup
            label={strings.directionLabel}
            name="tchart-direction"
            onChange={setDirection}
            options={(["portrait", "landscape"] as const).map((value) => ({
              value,
              label: strings.directions[value],
            }))}
            value={direction}
          />
        </div>
        <fieldset className="space-y-3">
          <legend className="font-medium text-foreground text-sm">
            {strings.entriesHeading}
          </legend>
          <p className={HINT}>{strings.entriesHint}</p>
          <div className="grid gap-4 sm:grid-cols-2">
            <TextAreaField
              id="tchart-left-entries"
              label={strings.leftEntries}
              onChange={setLeftText}
              prose
              rows={4}
              value={leftText}
            />
            <TextAreaField
              id="tchart-right-entries"
              label={strings.rightEntries}
              onChange={setRightText}
              prose
              rows={4}
              value={rightText}
            />
          </div>
        </fieldset>
        <fieldset className="space-y-2">
          <Checkbox
            checked={numbered}
            label={strings.numbered}
            onChange={setNumbered}
          />
          <Checkbox
            checked={nameLine}
            label={strings.nameLine}
            onChange={setNameLine}
          />
        </fieldset>
        {grew ? (
          <Notice>{t(strings.grew, { count: fmt(layout.count) })}</Notice>
        ) : null}
        {layout.cut > 0 ? (
          <Notice>
            {t(strings.cut, { max: fmt(MAX_ROWS), count: fmt(layout.cut) })}
          </Notice>
        ) : null}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 print:hidden">
        <h2 className={WIDGET_H2}>{strings.heading}</h2>
        <button
          className={BUTTON_PRIMARY}
          onClick={() => window.print()}
          type="button"
        >
          {strings.print}
        </button>
      </div>
      <TChartSheet
        dateLabel={strings.date}
        label={t(strings.sheetLabel, {
          left: cleanHeading(left),
          right: cleanHeading(right),
        })}
        leftHeading={cleanHeading(left)}
        nameLabel={strings.name}
        nameLine={nameLine}
        numbered={numbered}
        ratio={ratio}
        rightHeading={cleanHeading(right)}
        rows={layout}
        title={title.trim()}
      />
    </div>
  );
}
